import React, { useEffect, useState } from "react";
import {
  ArticleDto,
  ArticleHotNewsDto,
  ArticleType,
} from "../../dto/article.dto";
import { StepCheck } from "../progress";
import { useI18n } from "../../i18n/I18nProvider";
import ArticleImage from "./ArticleImage";
import NextArticle from "./NextArticle";
import ClaimButton from "../ui/ClaimButton";
import { articlesApi } from "../../api/articles.api";
import { useUser } from "../../hooks/useUser";

interface ArticleCardProps {
  name: string;
  article: ArticleDto;
}

interface ArticleDefaultContentProps {
  article: ArticleDto;
}

interface ArticleHotNewsContentProps {
  initialArticle: ArticleHotNewsDto;
}

const ArticleDefaultContent: React.FC<ArticleDefaultContentProps> = ({
  article,
}) => {
  const { t } = useI18n();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        marginBottom: "4px",
      }}
    >
      <StepCheck checked={article.isReaded} text={t("ReadTheArticle")} />
      <StepCheck
        checked={article.isQuizzesValidated}
        text={t("AnswerTheQuiz")}
      />
    </div>
  );
};

const ArticleHotNewsContent: React.FC<ArticleHotNewsContentProps> = ({
  initialArticle,
}) => {
  const { t } = useI18n();
  const [article, setArticle] = useState(initialArticle);
  const { addPoints } = useUser();

  const handleClaim = () => {
    articlesApi
      .claim(article.id, article.quest?.id)
      .then((articleClaimed) => {
        setArticle(articleClaimed);
        addPoints(articleClaimed.points.reading);
      })
      .catch((error) => {
        console.error("Error claiming article:", error);
      });
  };

  useEffect(() => {
    setArticle(initialArticle);
  }, [initialArticle]);

  return (
    <div
      className="flex"
      style={{ flexDirection: "row", gap: "8px", marginBottom: "12px" }}
    >
      <StepCheck checked={article.isReaded} text={t("ReadTheNews")} />
      <div style={{ width: "85%", marginRight: "8px" }}>
        <ClaimButton
          onClick={handleClaim}
          canBeClaimed={article.isReaded}
          isClaimed={article.isClaimed}
          points={article.points.reading}
        />
      </div>
    </div>
  );
};

const ArticleCard: React.FC<ArticleCardProps> = ({ name, article }) => {
  const [isCompleted, setIsCompleted] = useState(
    article.isReaded &&
      (article.type !== ArticleType.HOT_NEWS
        ? article.isQuizzesValidated
        : true)
  );
  const { t } = useI18n();

  const handleNextArticleClick = () => {
    if (isCompleted && article.nextArticle) {
      window.location.href = article.nextArticle.url;
    }
  };

  useEffect(() => {
    setIsCompleted(
      article.isReaded &&
        (article.type !== ArticleType.HOT_NEWS
          ? article.isQuizzesValidated
          : true)
    );
  }, [article]);

  return (
    <div
      className="flex flex-col items-center justify-start bg rounded w-full"
      style={{
        position: "relative",
        alignItems: "center",
        justifyContent: "flex-start",
      }}
    >
      <div
        className="text-white w-full text-center"
        style={{ marginTop: 0, marginBottom: 0, alignSelf: "flex-start" }}
      >
        {t("CurrentArticle")}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          width: "100%",
        }}
      >
        <ArticleImage
          image={article.image}
          title={article.title}
          height={100}
        />
        {article.type === ArticleType.HOT_NEWS ? (
          <ArticleHotNewsContent
            initialArticle={article as ArticleHotNewsDto}
          />
        ) : (
          <ArticleDefaultContent article={article} />
        )}
        {article.nextArticle && (
          <NextArticle
            image={article.nextArticle.image}
            title={article.nextArticle.title}
            isAvailable={isCompleted}
            onClick={handleNextArticleClick}
            nextTitle={
              article.type === ArticleType.HOT_NEWS
                ? t("NextHotNews")
                : t("NextArticle")
            }
          />
        )}
      </div>
    </div>
  );
};

export default ArticleCard;
